/**
 * Java compiler wrapper around CheerpJ (Java-to-JavaScript) with reliable stdout/stderr capture.
 *
 * Goals:
 * - Keep a simple public API: initialize, executeCode, executeCodeWithTestCases, isReady
 * - Make execution steps easy to follow
 * - Capture program output deterministically (stdout/stderr) with a clean fallback path
 * - Use CheerpJ for Java execution in the browser
 */

interface ExecutionResult {
  success: boolean;
  output: string;
  error?: string;
  executionTime: number;
  compilationError?: string;
  runtimeError?: string;
  errorType?: 'compilation' | 'runtime' | 'execution';
}

// CheerpJ global declarations
declare global {
  interface Window {
    cheerpjInit: (config: { version: number }) => Promise<void>;
    cheerpOSAddStringFile: (path: string, content: string | Uint8Array) => void;
    cheerpjAddStringFile: (path: string, content: ArrayBuffer) => void;
    cheerpjRunMain: (className: string, classpath: string, ...args: string[]) => Promise<number>;
    cheerpjRunJar: (jarPath: string, ...args: string[]) => Promise<number>;
  }
}

class JavaCompiler {
  // ——— Configuration ——————————————————————————————————————————————
  private static readonly WARMUP_DELAY_MS = 200;
  private static readonly OUTPUT_SETTLE_MS = 150;
  private static readonly NO_OUTPUT_TIMEOUT_MS = 2000;
  private static readonly JAVA_VERSION = 8;

  // ——— Runtime state ———————————————————————————————————————————————
  private isInitialized = false;
  private outputBuffer: string[] = [];
  private errorBuffer: string[] = [];
  private compilationErrorBuffer: string[] = [];
  private runtimeErrorBuffer: string[] = [];
  private consoleOutput: HTMLPreElement | null = null;
  private toolsJarLoaded = false;

  /**
   * Initialize CheerpJ Java runtime with proper output capture
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      this.resetBuffers();

      // Load CheerpJ loader script
      await this.loadCheerpJScript();
      
      // Initialize CheerpJ
      await window.cheerpjInit({ version: JavaCompiler.JAVA_VERSION });
      
      // Load tools.jar for compilation
      await this.loadToolsJar();
      
      // Create output console for capturing output
      this.createOutputConsole();
      
      // Warm up the compiler with a tiny program
      console.log('[java] warming up compiler...');
      await this.warmUpCompiler();
      
      this.isInitialized = true;
      console.log('[java] compiler initialized');

    } catch (error) {
      console.error('[java:error] compiler initialization failed:', error);
      throw error;
    }
  }

  /**
   * Load CheerpJ loader script
   */
  private async loadCheerpJScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cjrtnc.leaningtech.com/4.2/loader.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load CheerpJ.'));
      document.head.appendChild(script);
    });
  }

  /**
   * Load tools.jar for Java compilation
   */
  private async loadToolsJar(): Promise<void> {
    try {
      const toolsJarFile = await fetch('/tools.jar');
      if (!toolsJarFile.ok) {
        throw new Error(`Failed to fetch tools.jar: ${toolsJarFile.status} ${toolsJarFile.statusText}`);
      }
      
      // Use the same approach as the original slidev-addon-java-runner
      // Get the bytes directly as Uint8Array
      const toolsJarBytes = await toolsJarFile.arrayBuffer();
      const uint8Array = new Uint8Array(toolsJarBytes);
      
      // Use the correct path format that CheerpJ expects
      window.cheerpOSAddStringFile('/str/tools.jar', uint8Array);
      
      this.toolsJarLoaded = true;
      console.log('[java] tools.jar loaded successfully');
      
      // Wait a bit for the file to be processed
      await new Promise(resolve => setTimeout(resolve, 1000));
      
    } catch (error) {
      console.error('[java:error] failed to load tools.jar:', error);
      throw new Error('Failed to load Java compilation tools');
    }
  }

  /**
   * Create output console for capturing Java program output
   */
  private createOutputConsole(): void {
    // Remove existing console if any
    const existingConsole = document.getElementById('java-console');
    if (existingConsole) {
      existingConsole.remove();
    }

    // Create new console - use the same ID as the original slidev-addon-java-runner
    this.consoleOutput = document.createElement('pre');
    this.consoleOutput.id = 'console'; // Use 'console' to match CheerpJ's expected ID
    this.consoleOutput.style.cssText = 'position: absolute; left: -9999px; top: -9999px; visibility: hidden;';
    document.body.appendChild(this.consoleOutput);
    
    console.log('[java] output console created with ID "console"');
  }

  /**
   * Warm up the compiler with a simple program to initialize all streams
   */
  private async warmUpCompiler(): Promise<void> {
    console.log('[java] skipping warmup for now to avoid compilation issues');
    // Skip warmup for now to avoid compilation issues during initialization
    await new Promise(resolve => setTimeout(resolve, JavaCompiler.WARMUP_DELAY_MS));
    console.log('[java] warmup complete');
  }

  /**
   * Internal execution method that handles the actual Java compilation and execution
   */
  private async executeCodeInternal(code: string): Promise<ExecutionResult> {
    if (!this.isInitialized || !this.toolsJarLoaded) {
      throw new Error('Java compiler not initialized');
    }

    const startTime = performance.now();
    
    try {
      this.resetBuffers();
      
      // Clear console output
      if (this.consoleOutput) {
        this.consoleOutput.textContent = '';
      }

      // Extract class name from the code and create appropriate filename
      const extractedClassName = this.extractClassName(code);
      const fileName = `${extractedClassName}.java`;
      // Write source file to /str/ (the only filesystem cheerpOSAddStringFile supports)
      window.cheerpOSAddStringFile(`/str/${fileName}`, code);

      // Compile the Java code
      // Note: CheerpJ's /str/ filesystem is read-only for Java, but we can try outputting to /files/
      console.log('[java] compiling...');
      console.log('[java] code to compile:', code);
      
      try {
        // Clear the console before compilation
        if (this.consoleOutput) {
          this.consoleOutput.textContent = '';
        }
        
        const compileResult = await window.cheerpjRunMain(
          'com.sun.tools.javac.Main',
          '/str/tools.jar:/files/',
          '-d', '/files/',
          `/str/${fileName}`
        );

        console.log('[java] compilation result code:', compileResult);
        const compileOutput = this.consoleOutput?.textContent || '';
        console.log('[java] compilation output:', compileOutput);

        if (compileResult !== 0) {
          // Compilation failed - get detailed error
          const errorOutput = compileOutput || 'Compilation failed';
          const executionTime = performance.now() - startTime;
          
          console.error('[java] compilation failed with output:', errorOutput);
          
          return {
            success: false,
            output: '',
            error: errorOutput,
            compilationError: errorOutput,
            errorType: 'compilation',
            executionTime,
          };
        }
      } catch (compileError) {
        console.error('[java] compilation exception:', compileError);
        const executionTime = performance.now() - startTime;
        
        return {
          success: false,
          output: '',
          error: `Compilation exception: ${compileError}`,
          compilationError: `Compilation exception: ${compileError}`,
          errorType: 'compilation',
          executionTime,
        };
      }

      // Compilation successful, now run the program from /files/
      console.log('[java] running...');
      const className = this.deriveMainClass(fileName, code);
      console.log('[java] running class:', className);
      
      // Clear console before running
      if (this.consoleOutput) {
        this.consoleOutput.textContent = '';
      }
      
      try {
        const runResult = await window.cheerpjRunMain(
          className,
          '/files/'
        );

        console.log('[java] run result code:', runResult);

        // Wait for output to settle
        await new Promise(resolve => setTimeout(resolve, JavaCompiler.OUTPUT_SETTLE_MS));

        const output = this.consoleOutput?.textContent || '';
        const executionTime = performance.now() - startTime;

        if (runResult !== 0) {
          // Runtime error
          console.error('[java] runtime error, exit code:', runResult);
          return {
            success: false,
            output: output,
            error: `Process exited with code ${runResult}`,
            runtimeError: `Process exited with code ${runResult}`,
            errorType: 'runtime',
            executionTime,
          };
        }

        // Success
        console.log('[java] execution successful, output:', output);
        return {
          success: true,
          output: output || 'Program executed successfully',
          executionTime,
        };
      } catch (runError) {
        console.error('[java] runtime exception:', runError);
        const executionTime = performance.now() - startTime;
        
        return {
          success: false,
          output: '',
          error: `Runtime exception: ${runError}`,
          runtimeError: `Runtime exception: ${runError}`,
          errorType: 'runtime',
          executionTime,
        };
      }

    } catch (error) {
      const executionTime = performance.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Execution failed';
      
      console.error('[java:error] execution failed:', errorMessage);
      
      return {
        success: false,
        output: '',
        error: errorMessage,
        errorType: 'execution',
        executionTime,
      };
    }
  }

  /**
   * Execute Java code and return result
   */
  async executeCode(code: string): Promise<ExecutionResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return this.executeCodeInternal(code);
  }

  /**
   * Execute Java code with test cases
   */
  async executeCodeWithTestCases(
    userCode: string,
    testCases: { input: string; expectedOutput: string; fullExecutableCode: string }[]
  ): Promise<ExecutionResult[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const results: ExecutionResult[] = [];

    for (const testCase of testCases) {
      try {
        const fullProgram = await this.createFullCode(userCode, testCase.fullExecutableCode);
        console.log('----fullProgram----', fullProgram);
        
        const execResult = await this.executeCodeInternal(fullProgram);
        const cleanedOutput = await this.cleanOutput(execResult.output);
        execResult.output = cleanedOutput?.join('\n') || '';
        results.push(execResult);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Execution failed for test case';
        results.push({
          success: false,
          output: '',
          error: errorMessage,
          errorType: 'execution',
          executionTime: 0,
        });
      }
    }
    
    return results;
  }

  /**
   * Check if compiler is ready
   */
  isReady(): boolean {
    return this.isInitialized && this.toolsJarLoaded;
  }

  // ——— Helpers ————————————————————————————————————————————————
  private resetBuffers(): void {
    this.outputBuffer = [];
    this.errorBuffer = [];
    this.compilationErrorBuffer = [];
    this.runtimeErrorBuffer = [];
  }

  /**
   * Extract the class name from Java source code
   */
  private extractClassName(code: string): string {
    const classMatch = code.match(/public\s+class\s+(\w+)/);
    if (classMatch && classMatch[1]) {
      return classMatch[1];
    }
    // Fallback to Main if no class found
    return 'Main';
  }

  /**
   * Derive the main class name from file path and content
   */
  private deriveMainClass(filePath: string, fileContents: string): string {
    const className = filePath.split('/').pop()!.replace('.java', '');
    const match = fileContents.match(/package\s+(.+);/);
    
    if (match && match.length > 1) {
      const packageName = match[1];
      return `${packageName}.${className}`;
    } else {
      return className;
    }
  }

  /**
   * Create full executable code by injecting user code
   */
  async createFullCode(userCode: string, fullCode: string): Promise<string> {
    console.log('--------------------------------');
    console.log('userCode', userCode);
    console.log('--------------------------------');
    console.log('fullCode', fullCode);
    console.log('--------------------------------');
    
    // If fullCode is empty or doesn't contain the placeholder, use userCode directly
    // This allows running complete programs (like those with main() already defined)
    if (!fullCode || !fullCode.includes('// USER_FUNCTION_WILL_BE_INJECTED_HERE')) {
      console.log('[java] createFullCode - using userCode directly (no template)');
      return userCode;
    }

    let replacement = userCode;
    if (/\bclass\s+Main\b/.test(userCode)) {
      const extracted = this.extractMainClassBody(userCode);
      if (extracted) {
        replacement = extracted;
      }
    }

    const codeToExecute = fullCode.replace(
      '// USER_FUNCTION_WILL_BE_INJECTED_HERE',
      replacement.trim()
    );

    console.log('[java] createFullCode - final code:', codeToExecute)
    return codeToExecute;
  }

  /**
   * Extract the body of public class Main so we don't nest Main inside Main.
   */
  private extractMainClassBody(code: string): string | null {
    const classMatch = code.match(/\bclass\s+Main\b/);
    if (!classMatch || classMatch.index === undefined) return null;

    const startIndex = code.indexOf('{', classMatch.index);
    if (startIndex === -1) return null;

    let depth = 0;
    for (let i = startIndex; i < code.length; i += 1) {
      const char = code[i];
      if (char === '{') {
        depth += 1;
      } else if (char === '}') {
        depth -= 1;
        if (depth === 0) {
          return code.slice(startIndex + 1, i).trim();
        }
      }
    }

    return null;
  }

  /**
   * Clean output by removing ANSI escape codes and formatting
   */
  async cleanOutput(output: string): Promise<string[]> {
    const stripAnsi = (text: string) => text.replace(/\u001b\[[0-9;]*m/g, '');
    const cleanedOutput = stripAnsi(output || '')
      .split(/\r?\n/)
      .map(s => stripAnsi(s).trim())
      .filter(Boolean)
      .filter(s => !/^>\s*\d+\.wasm/.test(s));

    return cleanedOutput;
  }
}

// Export singleton
export const javaCompiler = new JavaCompiler();
export default javaCompiler;
